import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Input, Tooltip } from "antd";
import {
  AudioMutedOutlined,
  AudioOutlined,
  CloseOutlined,
  EditOutlined,
} from "@ant-design/icons";
import "./notesPanel.scss";
import { useNotesDictation } from "../../hooks/useNotesDictation";

export interface NotesPanelProps {
  caseId: string | undefined;
  caseDetailId: number | undefined;
  userRole: string | undefined;
  userId: number | undefined;
  /** User has the `voice_to_text` feature. */
  hasVoice: boolean;
  /** User has the `augnito_stt` feature (implies Augnito over Deepgram). */
  hasAugnito: boolean;
  /** Pass through so the FAB/panel can be hidden (e.g. report not allowed for this case). */
  visible: boolean;
  /** Invoked when the user clicks "Open report" — parent stashes text in ViewerNotesContext and opens the drawer. */
  onOpenReport: (text: string) => void;
}

const DEFAULT_WIDTH = 380;
const DEFAULT_HEIGHT = 460;
const MARGIN = 16;
const FAB_SIZE = 48;
// Matches .notes-fab defaults (right: 16px; bottom: 120px) so the panel opens
// above the FAB and above the DICOM metadata block it sits over.
const FAB_RIGHT_OFFSET = 16;
const FAB_BOTTOM_OFFSET = 120;
// Treat pointer movement below this threshold as a click, not a drag.
const DRAG_THRESHOLD_PX = 4;
const FAB_POS_STORAGE_KEY = "viewer-notes-fab-pos";

function storageKey(caseId: string | undefined) {
  return caseId ? `viewer-notes:${caseId}` : null;
}

function readStoredFabPos(): { x: number; y: number } | null {
  try {
    const raw = localStorage.getItem(FAB_POS_STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (typeof v?.x === "number" && typeof v?.y === "number") return v;
  } catch {
    /* noop */
  }
  return null;
}

function clamp(val: number, min: number, max: number) {
  return Math.min(max, Math.max(min, val));
}

/**
 * Floating FAB + draggable notes panel rendered by ViewerWrapper over the
 * DICOM viewer iframe. Text can be typed or dictated; "Save draft" stores
 * locally, "Open report" hands off to the report drawer via ViewerNotesContext.
 */
const NotesPanel: React.FC<NotesPanelProps> = ({
  caseId,
  caseDetailId,
  userRole,
  userId,
  hasVoice,
  hasAugnito,
  visible,
  onOpenReport,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState("");
  const [partial, setPartial] = useState("");
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const [fabPos, setFabPos] = useState<{ x: number; y: number } | null>(readStoredFabPos);

  const containerRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const fabRef = useRef<HTMLButtonElement>(null);
  const dragStateRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
  } | null>(null);
  const fabDragRef = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    moved: boolean;
  } | null>(null);
  // True briefly after a drag ends to suppress the synthetic click that follows.
  const suppressFabClickRef = useRef(false);

  const dictation = useNotesDictation({
    userRole,
    userId,
    caseDetailId,
    useAugnito: hasVoice && hasAugnito,
    onTranscript: (t) => setText((prev) => (prev + t).replace(/\s+/g, " ").trimStart()),
    onPartial: (p) => setPartial(p),
  });

  // Restore draft on mount / caseId change
  useEffect(() => {
    const key = storageKey(caseId);
    if (!key) return;
    try {
      const saved = localStorage.getItem(key);
      if (saved) setText(saved);
      else setText("");
    } catch {
      /* noop */
    }
  }, [caseId]);

  // Auto-persist text on change so unmounting (e.g. opening the report
  // drawer which hides the FAB) does not lose unsaved work.
  useEffect(() => {
    const key = storageKey(caseId);
    if (!key) return;
    try {
      if (text) localStorage.setItem(key, text);
    } catch {
      /* noop */
    }
  }, [caseId, text]);

  // Fetch quota once when panel opens (and voice feature is on)
  useEffect(() => {
    if (isOpen && hasVoice) dictation.fetchQuota();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasVoice]);

  // Stop dictation when panel closes
  useEffect(() => {
    if (!isOpen && dictation.isRecording) dictation.stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Default placement: anchor the panel near the FAB (above it, right-aligned
  // to its right edge) so it tracks the FAB's current position.
  useEffect(() => {
    if (!isOpen || pos) return;
    const parent = containerRef.current?.offsetParent as HTMLElement | null;
    const parentRect = parent?.getBoundingClientRect();
    const pw = parentRect?.width ?? window.innerWidth;
    const ph = parentRect?.height ?? window.innerHeight;

    let fabX: number;
    let fabY: number;
    if (fabPos) {
      fabX = fabPos.x;
      fabY = fabPos.y;
    } else {
      fabX = Math.max(MARGIN, pw - FAB_SIZE - FAB_RIGHT_OFFSET);
      fabY = Math.max(MARGIN, ph - FAB_SIZE - FAB_BOTTOM_OFFSET);
    }

    const rightEdge = fabX + FAB_SIZE;
    let x = clamp(rightEdge - DEFAULT_WIDTH, MARGIN, Math.max(MARGIN, pw - DEFAULT_WIDTH - MARGIN));
    let y = clamp(fabY - DEFAULT_HEIGHT - 8, MARGIN, Math.max(MARGIN, ph - DEFAULT_HEIGHT - MARGIN));
    // If there's no room above the FAB, place it below.
    if (fabY - DEFAULT_HEIGHT - 8 < MARGIN) {
      y = clamp(fabY + FAB_SIZE + 8, MARGIN, Math.max(MARGIN, ph - DEFAULT_HEIGHT - MARGIN));
    }

    setPos({ x, y });
  }, [isOpen, pos, fabPos]);

  // Reset position when caseId changes so new case gets default placement
  useEffect(() => {
    setPos(null);
  }, [caseId]);

  const onPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const el = containerRef.current;
    const header = headerRef.current;
    if (!el || !header) return;
    // Don't start drag on interactive elements (buttons)
    const target = e.target as HTMLElement;
    if (target.closest("button")) return;
    e.preventDefault();
    header.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    const parent = el.offsetParent as HTMLElement | null;
    const parentRect = parent?.getBoundingClientRect();
    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: rect.left - (parentRect?.left ?? 0),
      origY: rect.top - (parentRect?.top ?? 0),
    };
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const header = headerRef.current;
    const st = dragStateRef.current;
    if (!header || !st || !header.hasPointerCapture(e.pointerId)) return;
    e.preventDefault();
    const dx = e.clientX - st.startX;
    const dy = e.clientY - st.startY;
    const parent = containerRef.current?.offsetParent as HTMLElement | null;
    const parentRect = parent?.getBoundingClientRect();
    const pw = parentRect?.width ?? window.innerWidth;
    const ph = parentRect?.height ?? window.innerHeight;
    const x = clamp(st.origX + dx, 0, Math.max(0, pw - DEFAULT_WIDTH));
    const y = clamp(st.origY + dy, 0, Math.max(0, ph - DEFAULT_HEIGHT));
    setPos({ x, y });
  }, []);

  const onPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const header = headerRef.current;
    if (header?.hasPointerCapture(e.pointerId)) {
      header.releasePointerCapture(e.pointerId);
    }
    dragStateRef.current = null;
  }, []);

  // ─── FAB drag (with click/drag disambiguation) ───────────────────────────
  const onFabPointerDown = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const el = fabRef.current;
    if (!el) return;
    if (e.button !== 0 && e.pointerType === "mouse") return;
    el.setPointerCapture(e.pointerId);
    const rect = el.getBoundingClientRect();
    const parent = el.offsetParent as HTMLElement | null;
    const parentRect = parent?.getBoundingClientRect();
    fabDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: rect.left - (parentRect?.left ?? 0),
      origY: rect.top - (parentRect?.top ?? 0),
      moved: false,
    };
  }, []);

  const onFabPointerMove = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const el = fabRef.current;
    const st = fabDragRef.current;
    if (!el || !st || !el.hasPointerCapture(e.pointerId)) return;
    const dx = e.clientX - st.startX;
    const dy = e.clientY - st.startY;
    if (!st.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD_PX) return;
    st.moved = true;
    const parent = el.offsetParent as HTMLElement | null;
    const parentRect = parent?.getBoundingClientRect();
    const pw = parentRect?.width ?? window.innerWidth;
    const ph = parentRect?.height ?? window.innerHeight;
    const x = clamp(st.origX + dx, 0, Math.max(0, pw - FAB_SIZE));
    const y = clamp(st.origY + dy, 0, Math.max(0, ph - FAB_SIZE));
    setFabPos({ x, y });
  }, []);

  const onFabPointerUp = useCallback((e: React.PointerEvent<HTMLButtonElement>) => {
    const el = fabRef.current;
    const st = fabDragRef.current;
    if (el?.hasPointerCapture(e.pointerId)) {
      el.releasePointerCapture(e.pointerId);
    }
    fabDragRef.current = null;
    if (st?.moved) {
      // Persist the new position and swallow the synthetic click that follows.
      suppressFabClickRef.current = true;
      try {
        const current = fabPos;
        if (current) {
          localStorage.setItem(FAB_POS_STORAGE_KEY, JSON.stringify(current));
        }
      } catch {
        /* noop */
      }
      // Reset the suppression on the next tick (after the click fires).
      setTimeout(() => {
        suppressFabClickRef.current = false;
      }, 0);
    }
  }, [fabPos]);

  const handleFabClick = useCallback(() => {
    if (suppressFabClickRef.current) return;
    setIsOpen((v) => !v);
  }, []);

  const handleOpenReport = useCallback(() => {
    const trimmed = text.trim();
    if (dictation.isRecording) dictation.stop();
    const key = storageKey(caseId);
    if (key) {
      try {
        localStorage.removeItem(key);
      } catch {
        /* noop */
      }
    }
    setText("");
    setIsOpen(false);
    onOpenReport(trimmed);
  }, [text, dictation, caseId, onOpenReport]);

  const toggleRecord = useCallback(() => {
    if (dictation.isRecording) {
      dictation.stop();
    } else {
      dictation.start();
    }
  }, [dictation]);

  const panelStyle: React.CSSProperties = useMemo(
    () => ({
      left: pos?.x ?? 0,
      top: pos?.y ?? 0,
      width: DEFAULT_WIDTH,
      visibility: pos ? "visible" : "hidden",
    }),
    [pos]
  );

  // When the FAB has been dragged, override the CSS defaults (right/bottom)
  // with explicit left/top so it sticks where the user dropped it.
  const fabStyle: React.CSSProperties | undefined = fabPos
    ? { left: fabPos.x, top: fabPos.y, right: "auto", bottom: "auto" }
    : undefined;

  const displayValue = partial ? `${text} ${partial}` : text;

  if (!visible) return null;

  return (
    <>
      {/* Quick-notes FAB hidden for now — will be released in the next version. */}
      {/*
      <Tooltip title={isOpen ? "Close notes" : "Quick notes (drag to move)"} placement="left">
        <button
          ref={fabRef}
          type="button"
          className={`notes-fab${isOpen ? " notes-fab--active" : ""}`}
          style={fabStyle}
          onPointerDown={onFabPointerDown}
          onPointerMove={onFabPointerMove}
          onPointerUp={onFabPointerUp}
          onPointerCancel={onFabPointerUp}
          onClick={handleFabClick}
          aria-label={isOpen ? "Close quick notes" : "Open quick notes"}
        >
          {isOpen ? <CloseOutlined /> : <EditOutlined />}
        </button>
      </Tooltip>
      */}

      {isOpen && (
        <div
          ref={containerRef}
          className="notes-panel"
          style={panelStyle}
          role="dialog"
          aria-label="Quick notes"
        >
          <div
            ref={headerRef}
            className="notes-panel__header"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          >
            <span className="notes-panel__drag-dots" aria-hidden>
              ⋮⋮
            </span>
            <span className="notes-panel__title">Quick notes</span>
            {hasVoice && (
              <Tooltip
                title={dictation.isRecording ? "Stop dictation" : "Start dictation"}
              >
                <button
                  type="button"
                  className={`notes-panel__mic${
                    dictation.isRecording ? " notes-panel__mic--active" : ""
                  }`}
                  onClick={toggleRecord}
                  disabled={dictation.isLoading}
                  aria-label="Toggle dictation"
                >
                  {dictation.isRecording ? (
                    <AudioOutlined />
                  ) : (
                    <AudioMutedOutlined />
                  )}
                </button>
              </Tooltip>
            )}
            <button
              type="button"
              className="notes-panel__close"
              onClick={() => setIsOpen(false)}
              aria-label="Close notes panel"
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="notes-panel__body">
            <Input.TextArea
              className="notes-panel__textarea"
              placeholder="Jot findings here. Use the mic to dictate."
              value={displayValue}
              onChange={(e) => {
                if (dictation.isRecording && partial) {
                  return;
                }
                setText(e.target.value);
              }}
              autoSize={{ minRows: 12, maxRows: 18 }}
            />
            {dictation.isRecording && (
              <div className="notes-panel__status">
                <span className="notes-panel__status-dot" />
                Recording · {dictation.sessionSeconds}s
              </div>
            )}
          </div>

          <div className="notes-panel__footer">
            <Button
              size="small"
              type="primary"
              disabled={!text.trim() || dictation.isRecording}
              onClick={handleOpenReport}
            >
              Open report
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default NotesPanel;
